import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import "../../index.css";
import SummaryView from "../views/SummaryView";
import BrandDetailView from "../views/BrandDetailView";
import CreatorDetailView from "../views/CreatorDetailView";
import { useSocialGlassData } from "../hooks/useSocialGlassData";
import { useVideoControls } from "../hooks/useVideoControls";
import { 
  transformTopBrands, 
  transformTopCreators, 
  transformPosts, 
  transformBreakoutPosts,
  transformBrandSentiments,
  transformCreatorSentiments,
  getBrandPosts,
  getBrandComments,
  getCreatorPosts
} from "../utils/dataTransformers";
import { Brand, Creator } from "../types/socialGlass";

const SocialGlassSummary: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [postIndex, setPostIndex] = useState(0);
  const [brandPostIndex, setBrandPostIndex] = useState(0);
  const [creatorPostIndex, setCreatorPostIndex] = useState(0);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllCreators, setShowAllCreators] = useState(false);
  
  const { summaryData, idToData, loading, error } = useSocialGlassData();
  const { videoRefs, handleManualMute, handleManualUnmute } = useVideoControls();

  // Initialize selected category when data loads
  React.useEffect(() => {
    if (summaryData?.TopPosts && !selectedCategory) {
      setSelectedCategory(Object.keys(summaryData.TopPosts)[0]);
    }
  }, [summaryData, selectedCategory]);

  // Transform the data using imported functions
  const topBrands = transformTopBrands(summaryData);
  const topCreators = transformTopCreators(summaryData);
  const posts = selectedCategory ? transformPosts(summaryData, selectedCategory) : [];
  const breakoutPosts = selectedCategory ? transformBreakoutPosts(summaryData, selectedCategory) : [];
  const brandSentiments = transformBrandSentiments(summaryData);
  const creatorSentiments = transformCreatorSentiments(summaryData);
  const brandPosts = selectedBrand ? getBrandPosts(summaryData, selectedBrand.name) : [];
  const brandComments = selectedBrand ? getBrandComments(summaryData, selectedBrand.name) : [];
  const creatorPosts = selectedCreator ? getCreatorPosts(summaryData, selectedCreator.name) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">Loading...</div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-6 text-red-500">
        Error: {error}
      </div>
    );
  }

  // Helper for highlighting
  const isBrandSelected = (brand: Brand): boolean =>
    selectedBrand && selectedBrand.name === brand.name && !selectedCreator;
  const isCreatorSelected = (creator: Creator): boolean =>
    selectedCreator && selectedCreator.name === creator.name && !selectedBrand;

  return (
    <AnimatePresence mode="wait">
      {!selectedBrand && !selectedCreator ? (
        <SummaryView
          topBrands={topBrands}
          topCreators={topCreators}
          posts={posts}
          breakoutPosts={breakoutPosts}
          selectedCategory={selectedCategory}
          selectedBrand={selectedBrand}
          selectedCreator={selectedCreator}
          showAllBrands={showAllBrands}
          showAllCreators={showAllCreators}
          idToData={idToData}
          videoRefs={videoRefs}
          handleManualMute={handleManualMute}
          handleManualUnmute={handleManualUnmute}
          onBrandSelect={setSelectedBrand}
          onCreatorSelect={(creator) => {
            setSelectedCreator(creator);
            setCreatorPostIndex(0);
            setSelectedBrand(null);
          }}
          onCategorySelect={setSelectedCategory}
          onToggleShowAllBrands={() => setShowAllBrands(!showAllBrands)}
          onToggleShowAllCreators={() => setShowAllCreators(!showAllCreators)}
          isBrandSelected={isBrandSelected}
          isCreatorSelected={isCreatorSelected}
          summaryData={summaryData}
        />
      ) : selectedBrand ? (
        <BrandDetailView
          selectedBrand={selectedBrand}
          topBrands={topBrands}
          brandPosts={brandPosts}
          brandComments={brandComments}
          brandSentiments={brandSentiments}
          idToData={idToData}
          videoRefs={videoRefs}
          handleManualMute={handleManualMute}
          handleManualUnmute={handleManualUnmute}
          onBrandSelect={setSelectedBrand}
          onBack={() => setSelectedBrand(null)}
          isBrandSelected={isBrandSelected}
        />
      ) : (
        <CreatorDetailView
          selectedCreator={selectedCreator}
          topCreators={topCreators}
          creatorPosts={creatorPosts}
          creatorSentiments={creatorSentiments}
          idToData={idToData}
          videoRefs={videoRefs}
          handleManualMute={handleManualMute}
          handleManualUnmute={handleManualUnmute}
          onCreatorSelect={(creator) => {
            setSelectedCreator(creator);
            setCreatorPostIndex(0);
            setSelectedBrand(null);
          }}
          onBack={() => setSelectedCreator(null)}
          isCreatorSelected={isCreatorSelected}
        />
      )}
    </AnimatePresence>
  );
};

export default SocialGlassSummary;